// PWA helpers: service-worker registration + offline mutation queue.
//
// Public API:
//   registerServiceWorker()  — registers /sw.js (idempotent, safe in dev)
//   isOnline()               — current navigator.onLine
//   onConnectionChange(fn)   — subscribe to online/offline events
//   queueCheckIn(eventId, fn) — runs fn() if online, otherwise persists the call
//                               and replays it as soon as the browser comes back online
//   getQueueLength()         — number of pending offline ops

const QUEUE_KEY = 'rivl:offline-queue';

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // Avoid registering on localhost dev to keep hot-reload sane
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalDev && !window.__RIVL_FORCE_SW__) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* silent — sw is best-effort */
    });
  });
}

export function isOnline() {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export function onConnectionChange(cb) {
  const on = () => cb(true);
  const off = () => cb(false);
  window.addEventListener('online', on);
  window.addEventListener('offline', off);
  return () => {
    window.removeEventListener('online', on);
    window.removeEventListener('offline', off);
  };
}

function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
}
function writeQueue(items) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
}

export function getQueueLength() {
  return readQueue().length;
}

/**
 * Try to perform a mutation; if offline, push it onto a localStorage queue
 * and replay on reconnect.
 *
 * @param {string} kind          stable label for the operation (e.g. "checkin")
 * @param {string} eventId       event ID
 * @param {Function} runner      async function that performs the mutation; called with no args
 * @returns {Promise<{queued: boolean}>}
 */
export async function queueOperation(kind, eventId, runner) {
  if (isOnline()) {
    await runner();
    return { queued: false };
  }
  const items = readQueue();
  items.push({ kind, eventId, ts: Date.now() });
  writeQueue(items);
  return { queued: true };
}

// Replay queued check-ins. Caller passes a map of kind → runner so the
// queue file doesn't need to know about API code.
export async function flushQueue(handlers) {
  const items = readQueue();
  if (!items.length) return { flushed: 0, errors: 0 };
  const remaining = [];
  let flushed = 0;
  let errors = 0;
  for (const item of items) {
    const fn = handlers[item.kind];
    if (!fn) { remaining.push(item); continue; }
    try {
      await fn(item);
      flushed += 1;
    } catch {
      errors += 1;
      remaining.push(item);
    }
  }
  writeQueue(remaining);
  return { flushed, errors };
}
